import { useCallback, useMemo } from 'react';
import { DateObjectUnits, DateTime } from 'luxon';
import { useFormContext, useWatch } from 'react-hook-form';
import { useFeatureFlag } from 'modules/common/hooks';
import { SPLIT_NOTE_FIELDS } from '../types';
import { useSplitEntriesContext } from '../SplitEntriesProvider';

type ReturnType = {
  startTimeChangeHandler: (date: DateTime | null) => void;
  endTimeChangeHandler: (date: DateTime | null) => void;
  durationChangeHandler: (duration: number | null) => void;
};

export const useTimeHandlers = ({
  date,
  position,
  startTime,
  endTime
}: {
  date: Date;
  position: number;
  startTime?: Date | null;
  endTime?: Date | null;
}): ReturnType => {
  const { setValue, trigger, control } = useFormContext();
  const isTiwDurationIndependentStartEndTime = useFeatureFlag(
    'isTiwDurationIndependentStartEndTime'
  );
  const startTimeName = `${SPLIT_NOTE_FIELDS.SPLIT_ENTRIES}.${position}.${SPLIT_NOTE_FIELDS.START_TIME}`;
  const endTimeName = `${SPLIT_NOTE_FIELDS.SPLIT_ENTRIES}.${position}.${SPLIT_NOTE_FIELDS.END_TIME}`;
  const durationName = `${SPLIT_NOTE_FIELDS.SPLIT_ENTRIES}.${position}.${SPLIT_NOTE_FIELDS.DURATION}`;
  const { adjustEntriesHandler } = useSplitEntriesContext();
  const triggerFields = useMemo(
    () => [startTimeName, endTimeName, durationName],
    [startTimeName, endTimeName, durationName]
  );
  const currentDuration = useWatch({ name: durationName, control });

  const sourceDateUnits = useMemo(() => {
    const units: DateObjectUnits = {
      ...(date
        ? {
            day: date.getDate(),
            month: date.getMonth() + 1,
            year: date.getFullYear()
          }
        : {})
    };

    return units;
  }, [date]);

  const calculateDuration = useCallback(
    (startTime: DateTime | null, endTime: DateTime | null) => {
      if (startTime && endTime && startTime < endTime) {
        const duration = endTime.diff(startTime, [
          'hours',
          'minutes',
          'seconds'
        ]);
        setValue(durationName, duration.shiftTo('seconds').toObject().seconds, {
          shouldDirty: true
        });
      } else {
        setValue(durationName, 0, {
          shouldDirty: true
        });
      }
    },
    [durationName, setValue]
  );

  const startTimeChangeHandler = useCallback(
    (date: DateTime | null) => {
      const start = date
        ? date.set({
            ...sourceDateUnits,
            second: 0,
            millisecond: 0
          })
        : null;
      setValue(startTimeName, start?.toJSDate(), {
        shouldDirty: true
      });
      const end = endTime
        ? DateTime.fromJSDate(endTime).set({ ...sourceDateUnits })
        : null;

      calculateDuration(start, end);
      adjustEntriesHandler();
      trigger(triggerFields);
    },
    [
      sourceDateUnits,
      setValue,
      startTimeName,
      endTime,
      calculateDuration,
      adjustEntriesHandler,
      trigger,
      triggerFields
    ]
  );

  const endTimeChangeHandler = useCallback(
    (date: DateTime | null) => {
      const end = date
        ? date.set({
            ...sourceDateUnits,
            second: 0,
            millisecond: 0
          })
        : null;

      setValue(endTimeName, end?.toJSDate(), { shouldDirty: true });
      const start = startTime
        ? DateTime.fromJSDate(startTime).set({ ...sourceDateUnits })
        : null;

      calculateDuration(start, end);
      adjustEntriesHandler();
      trigger(triggerFields);
    },
    [
      sourceDateUnits,
      setValue,
      endTimeName,
      startTime,
      calculateDuration,
      adjustEntriesHandler,
      trigger,
      triggerFields
    ]
  );

  const durationChangeHandler = useCallback(
    (duration: number | null) => {
      if (currentDuration === duration) {
        return;
      }
      setValue(durationName, duration ? Math.floor(duration ?? 0) : null, {
        shouldDirty: true
      });

      if (!isTiwDurationIndependentStartEndTime) {
        setValue(startTimeName, null);
        setValue(endTimeName, null);
      }

      adjustEntriesHandler();
      trigger(triggerFields);
    },
    [
      currentDuration,
      setValue,
      durationName,
      isTiwDurationIndependentStartEndTime,
      adjustEntriesHandler,
      trigger,
      triggerFields,
      startTimeName,
      endTimeName
    ]
  );

  return {
    startTimeChangeHandler,
    endTimeChangeHandler,
    durationChangeHandler
  };
};
